import type { Cell } from 'write-excel-file/browser';

const DATE_FORMAT = 'mm/dd/yyyy';

/**
 * Normalises an arbitrary accessor value into a cell `write-excel-file` accepts.
 *
 * `Accessor<T>` returns `unknown` and `createAccessor` passes `row[key]` through
 * untouched, so any JS value can reach the writer. `write-excel-file` only accepts
 * `String | Number | Boolean | Date | null`, and without normalising it would:
 *   - throw on a `Date` that has no explicit `format`
 *   - write a plain object as a silently empty cell, losing data
 *   - write `NaN`/`Infinity` into a numeric cell, producing a workbook Excel repairs
 */
export function toExcelCell(value: unknown): Cell {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : String(value);
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { value, type: Date, format: DATE_FORMAT };
  }

  if (typeof value === 'object') {
    // exceljs stored arbitrary objects as ValueType.JSON, i.e. JSON.stringify output.
    // The State, Underwriter and Tags columns are arrays and depend on that shape, so
    // the JSON representation is preserved rather than coerced with String().
    try {
      return JSON.stringify(value) ?? String(value);
    } catch {
      // Circular references and bigint members make JSON.stringify throw.
      return String(value);
    }
  }

  return String(value);
}
